import { Status } from '@pillar-portal/database/dist/enums';
import { z } from 'zod';
import {
    OpaqueCursorPaginationSchema,
    type OpaqueCursorPagination,
} from './authorization.types';
import {
    AllowedWebsiteSchemaUnmanaged,
    type UnmanagedAllowedWebsite,
} from './backend_config.types';
import { defaultIntegratedWebsiteSystemIds } from './backend_config.constants';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import {
    AuthorizationOpaqueCursorQuerySchema,
    AuthorizationPaginationLimitQuerySchema,
} from './schemas/authorization.schemas';
import { SearchTermsSchema } from './schemas/omnisearch.schemas';
import { ThirdPartySystemId } from './types/third-party-system.enums';

export type AllowedWebsiteTemplateSystemId =
    | typeof ThirdPartySystemId.GENERIC
    | (typeof defaultIntegratedWebsiteSystemIds)[number];

const AllowedWebsiteTemplateSystemIdSet: ReadonlySet<number> = new Set([
    ThirdPartySystemId.GENERIC,
    ...defaultIntegratedWebsiteSystemIds,
]);

export function isAllowedWebsiteTemplateSystemId(
    systemId: number
): systemId is AllowedWebsiteTemplateSystemId {
    return AllowedWebsiteTemplateSystemIdSet.has(systemId);
}

export const AllowedWebsiteTemplateSystemIdSchema: z.ZodType<AllowedWebsiteTemplateSystemId> =
    z
        .number()
        .int()
        .refine(
            isAllowedWebsiteTemplateSystemId,
            'Unsupported allowed website template system id.'
        );

export interface AllowedWebsiteTemplateCapabilities {
    can_display: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_disable: boolean;
    can_enable: boolean;
    can_use: boolean;
    can_audit: boolean;
}

export interface AllowedWebsiteTemplateCollectionCapabilities {
    can_create: boolean;
    can_show_disabled: boolean;
}

export interface AllowedWebsiteTemplateBlastRadiusSummary {
    active_merchant_count: number;
}

export interface AllowedWebsiteTemplateListItem {
    allowed_website_template_uuid: string;
    ipx_system_id: number;
    name: string;
    status: Status;
    primary_url: string;
    website_config: UnmanagedAllowedWebsite;
    blast_radius: AllowedWebsiteTemplateBlastRadiusSummary;
    capabilities: AllowedWebsiteTemplateCapabilities;
}

export interface AllowedWebsiteTemplateSearchRequest
    extends OpaqueCursorSearchRequest {
    ipx_system_id?: number;
    status?: Status;
    show_disabled?: boolean;
}

export interface AllowedWebsiteTemplateSearchPageResponse {
    templates: AllowedWebsiteTemplateListItem[];
    pagination: OpaqueCursorPagination;
    capabilities: AllowedWebsiteTemplateCollectionCapabilities;
}

export interface CreateAllowedWebsiteTemplateRequest {
    ipx_system_id: AllowedWebsiteTemplateSystemId;
    name: string;
    website_config: UnmanagedAllowedWebsite;
}

export interface UpdateAllowedWebsiteTemplateRequest {
    ipx_system_id?: AllowedWebsiteTemplateSystemId;
    name?: string;
    website_config?: UnmanagedAllowedWebsite;
    expected_active_merchant_count?: number;
}

export interface AllowedWebsiteTemplateUuidRequest {
    allowed_website_template_uuid: string;
}

export interface AllowedWebsiteTemplateMutationResponse {
    template: AllowedWebsiteTemplateListItem;
}

export interface AllowedWebsiteTemplateBlastRadiusResponse {
    allowed_website_template_uuid: string;
    blast_radius: AllowedWebsiteTemplateBlastRadiusSummary;
}

export interface SafeDeleteAllowedWebsiteTemplateResponse {
    allowed_website_template_uuid: string;
    deleted: true;
}

export const OrganizationAllowedWebsiteSource = {
    LOCAL: 'LOCAL',
    TEMPLATE: 'TEMPLATE',
} as const;
export type OrganizationAllowedWebsiteSource =
    (typeof OrganizationAllowedWebsiteSource)[keyof typeof OrganizationAllowedWebsiteSource];

export interface AllowedWebsiteTemplateSummary {
    allowed_website_template_uuid: string;
    ipx_system_id: number;
    name: string;
    status: Status;
}

export interface AllowedWebsiteTemplateProvenanceCapabilities {
    can_view_template: boolean;
}

export interface AllowedWebsiteTemplateProvenance {
    allowed_website_template_uuid: string;
    ipx_system_id: number;
    name: string;
    status: Status;
    capabilities: AllowedWebsiteTemplateProvenanceCapabilities;
}

export interface OrganizationAllowedWebsiteCapabilities {
    can_edit: boolean;
    can_delete: boolean;
    can_unlink_template: boolean;
    can_customize_template: boolean;
    can_promote_to_template: boolean;
    can_view_template: boolean;
}

export interface OrganizationAllowedWebsiteCollectionCapabilities {
    can_create_local: boolean;
    can_reference_template: boolean;
}

export interface OrganizationAllowedWebsiteBase {
    organization_allowed_website_uuid: string | null;
    organization_uuid: string;
    status: Status;
    display_order: number | null;
    capabilities: OrganizationAllowedWebsiteCapabilities;
}

export interface LocalOrganizationAllowedWebsite
    extends OrganizationAllowedWebsiteBase {
    source: typeof OrganizationAllowedWebsiteSource.LOCAL;
    organization_allowed_website_uuid: string;
    website_config: UnmanagedAllowedWebsite;
    template: null;
    copied_from_template: AllowedWebsiteTemplateSummary | null;
    template_provenance?: AllowedWebsiteTemplateProvenance | null;
}

export interface TemplateOrganizationAllowedWebsite
    extends OrganizationAllowedWebsiteBase {
    source: typeof OrganizationAllowedWebsiteSource.TEMPLATE;
    organization_allowed_website_uuid: string;
    included_in_runtime_config: boolean;
    website_config: UnmanagedAllowedWebsite;
    template: AllowedWebsiteTemplateSummary;
    copied_from_template: null;
}

export type TemplateReferenceOrganizationAllowedWebsite =
    TemplateOrganizationAllowedWebsite;

// Discriminator: source
export type OrganizationAllowedWebsite =
    | LocalOrganizationAllowedWebsite
    | TemplateOrganizationAllowedWebsite;

export interface ListOrganizationAllowedWebsitesResponse {
    websites: OrganizationAllowedWebsite[];
    capabilities: OrganizationAllowedWebsiteCollectionCapabilities;
}

export interface CreateLocalOrganizationAllowedWebsiteRequest {
    website_config: UnmanagedAllowedWebsite;
    display_order?: number | null;
}

export interface ReferenceAllowedWebsiteTemplateToOrganizationRequest {
    allowed_website_template_uuid: string;
    display_order?: number | null;
}

export interface UpdateOrganizationAllowedWebsiteRequest {
    website_config?: UnmanagedAllowedWebsite;
    display_order?: number | null;
}

export interface OrganizationAllowedWebsiteMutationResponse {
    organization_website: OrganizationAllowedWebsite;
}

export interface DeleteOrganizationAllowedWebsiteResponse {
    organization_allowed_website_uuid: string;
    deleted: true;
}

export interface PromoteOrganizationWebsiteToTemplateRequest {
    organization_allowed_website_uuid: string;
    ipx_system_id: number;
    template_name: string;
}

export interface PromoteOrganizationWebsiteToTemplateResponse {
    template: AllowedWebsiteTemplateListItem;
    organization_website: OrganizationAllowedWebsite;
}

export const AllowedWebsiteTemplateCapabilitiesSchema: z.ZodType<AllowedWebsiteTemplateCapabilities> =
    z.object({
        can_display: z.boolean(),
        can_edit: z.boolean(),
        can_delete: z.boolean(),
        can_disable: z.boolean(),
        can_enable: z.boolean(),
        can_use: z.boolean(),
        can_audit: z.boolean(),
    });

export const AllowedWebsiteTemplateCollectionCapabilitiesSchema: z.ZodType<AllowedWebsiteTemplateCollectionCapabilities> =
    z.object({
        can_create: z.boolean(),
        can_show_disabled: z.boolean(),
    });

export const AllowedWebsiteTemplateBlastRadiusSummarySchema: z.ZodType<AllowedWebsiteTemplateBlastRadiusSummary> =
    z.object({
        active_merchant_count: z.number().int().nonnegative(),
    });

export const AllowedWebsiteTemplateListItemSchema: z.ZodType<AllowedWebsiteTemplateListItem> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        ipx_system_id: z.number().int().nonnegative(),
        name: z.string().trim().min(1),
        status: z.enum(Status),
        primary_url: z.string().url(),
        website_config: AllowedWebsiteSchemaUnmanaged,
        blast_radius: AllowedWebsiteTemplateBlastRadiusSummarySchema,
        capabilities: AllowedWebsiteTemplateCapabilitiesSchema,
    });

export const AllowedWebsiteTemplateSearchRequestSchema: z.ZodType<AllowedWebsiteTemplateSearchRequest> =
    z
        .object({
            terms: SearchTermsSchema,
            limit: AuthorizationPaginationLimitQuerySchema,
            cursor: AuthorizationOpaqueCursorQuerySchema,
            ipx_system_id: z.number().int().nonnegative().optional(),
            status: z.enum(Status).optional(),
            show_disabled: z.boolean().optional(),
        })
        .strict();

export const AllowedWebsiteTemplateSearchPageResponseSchema: z.ZodType<AllowedWebsiteTemplateSearchPageResponse> =
    z.object({
        templates: z.array(AllowedWebsiteTemplateListItemSchema),
        pagination: OpaqueCursorPaginationSchema,
        capabilities: AllowedWebsiteTemplateCollectionCapabilitiesSchema,
    });

export const CreateAllowedWebsiteTemplateRequestSchema: z.ZodType<CreateAllowedWebsiteTemplateRequest> =
    z
        .object({
            ipx_system_id: AllowedWebsiteTemplateSystemIdSchema,
            name: z.string().trim().min(1),
            website_config: AllowedWebsiteSchemaUnmanaged,
        })
        .strict();

export const UpdateAllowedWebsiteTemplateRequestSchema: z.ZodType<UpdateAllowedWebsiteTemplateRequest> =
    z
        .object({
            ipx_system_id: AllowedWebsiteTemplateSystemIdSchema.optional(),
            name: z.string().trim().min(1).optional(),
            website_config: AllowedWebsiteSchemaUnmanaged.optional(),
            expected_active_merchant_count: z.number().int().nonnegative().optional(),
        })
        .strict()
        .refine(
            (request) =>
                request.ipx_system_id !== undefined ||
                request.name !== undefined ||
                request.website_config !== undefined,
            'At least one template field must be updated.'
        );

export const AllowedWebsiteTemplateUuidRequestSchema: z.ZodType<AllowedWebsiteTemplateUuidRequest> =
    z
        .object({
            allowed_website_template_uuid: z.string().uuid(),
        })
        .strict();

export const EnableAllowedWebsiteTemplateRequestSchema: z.ZodType<AllowedWebsiteTemplateUuidRequest> =
    AllowedWebsiteTemplateUuidRequestSchema;

export const DisableAllowedWebsiteTemplateRequestSchema: z.ZodType<AllowedWebsiteTemplateUuidRequest> =
    AllowedWebsiteTemplateUuidRequestSchema;

export const SafeDeleteAllowedWebsiteTemplateRequestSchema: z.ZodType<AllowedWebsiteTemplateUuidRequest> =
    AllowedWebsiteTemplateUuidRequestSchema;

export const AllowedWebsiteTemplateMutationResponseSchema: z.ZodType<AllowedWebsiteTemplateMutationResponse> =
    z.object({
        template: AllowedWebsiteTemplateListItemSchema,
    });

export const AllowedWebsiteTemplateBlastRadiusResponseSchema: z.ZodType<AllowedWebsiteTemplateBlastRadiusResponse> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        blast_radius: AllowedWebsiteTemplateBlastRadiusSummarySchema,
    });

export const SafeDeleteAllowedWebsiteTemplateResponseSchema: z.ZodType<SafeDeleteAllowedWebsiteTemplateResponse> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        deleted: z.literal(true),
    });

export const OrganizationAllowedWebsiteSourceSchema: z.ZodType<OrganizationAllowedWebsiteSource> =
    z.enum([
        OrganizationAllowedWebsiteSource.LOCAL,
        OrganizationAllowedWebsiteSource.TEMPLATE,
    ]);

export const AllowedWebsiteTemplateSummarySchema: z.ZodType<AllowedWebsiteTemplateSummary> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        ipx_system_id: z.number().int().nonnegative(),
        name: z.string().trim().min(1),
        status: z.enum(Status),
    });

export const AllowedWebsiteTemplateProvenanceCapabilitiesSchema: z.ZodType<AllowedWebsiteTemplateProvenanceCapabilities> =
    z.object({
        can_view_template: z.boolean(),
    });

export const AllowedWebsiteTemplateProvenanceSchema: z.ZodType<AllowedWebsiteTemplateProvenance> =
    z.object({
        allowed_website_template_uuid: z.string().uuid(),
        ipx_system_id: z.number().int().nonnegative(),
        name: z.string().trim().min(1),
        status: z.enum(Status),
        capabilities: AllowedWebsiteTemplateProvenanceCapabilitiesSchema,
    });

export const OrganizationAllowedWebsiteCapabilitiesSchema: z.ZodType<OrganizationAllowedWebsiteCapabilities> =
    z.object({
        can_edit: z.boolean(),
        can_delete: z.boolean(),
        can_unlink_template: z.boolean(),
        can_customize_template: z.boolean(),
        can_promote_to_template: z.boolean(),
        can_view_template: z.boolean(),
    });

export const OrganizationAllowedWebsiteCollectionCapabilitiesSchema: z.ZodType<OrganizationAllowedWebsiteCollectionCapabilities> =
    z.object({
        can_create_local: z.boolean(),
        can_reference_template: z.boolean(),
    });

export const LocalOrganizationAllowedWebsiteSchema: z.ZodObject<{
    organization_allowed_website_uuid: z.ZodString;
    organization_uuid: z.ZodString;
    status: z.ZodEnum<typeof Status>;
    display_order: z.ZodNullable<z.ZodNumber>;
    capabilities: typeof OrganizationAllowedWebsiteCapabilitiesSchema;
    source: z.ZodLiteral<typeof OrganizationAllowedWebsiteSource.LOCAL>;
    website_config: typeof AllowedWebsiteSchemaUnmanaged;
    template: z.ZodNull;
    copied_from_template: z.ZodNullable<
        typeof AllowedWebsiteTemplateSummarySchema
    >;
    template_provenance: z.ZodOptional<
        z.ZodNullable<typeof AllowedWebsiteTemplateProvenanceSchema>
    >;
}> =
    z.object({
        organization_allowed_website_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid(),
        status: z.enum(Status),
        display_order: z.number().int().nonnegative().nullable(),
        capabilities: OrganizationAllowedWebsiteCapabilitiesSchema,
        source: z.literal(OrganizationAllowedWebsiteSource.LOCAL),
        website_config: AllowedWebsiteSchemaUnmanaged,
        template: z.null(),
        copied_from_template: AllowedWebsiteTemplateSummarySchema.nullable(),
        template_provenance: AllowedWebsiteTemplateProvenanceSchema.nullable().optional(),
    });

export const TemplateOrganizationAllowedWebsiteSchema: z.ZodObject<{
    organization_allowed_website_uuid: z.ZodString;
    organization_uuid: z.ZodString;
    status: z.ZodEnum<typeof Status>;
    display_order: z.ZodNullable<z.ZodNumber>;
    capabilities: typeof OrganizationAllowedWebsiteCapabilitiesSchema;
    source: z.ZodLiteral<typeof OrganizationAllowedWebsiteSource.TEMPLATE>;
    included_in_runtime_config: z.ZodBoolean;
    website_config: typeof AllowedWebsiteSchemaUnmanaged;
    template: typeof AllowedWebsiteTemplateSummarySchema;
    copied_from_template: z.ZodNull;
}> =
    z.object({
        organization_allowed_website_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid(),
        status: z.enum(Status),
        display_order: z.number().int().nonnegative().nullable(),
        capabilities: OrganizationAllowedWebsiteCapabilitiesSchema,
        source: z.literal(OrganizationAllowedWebsiteSource.TEMPLATE),
        included_in_runtime_config: z.boolean(),
        website_config: AllowedWebsiteSchemaUnmanaged,
        template: AllowedWebsiteTemplateSummarySchema,
        copied_from_template: z.null(),
    });

export const TemplateReferenceOrganizationAllowedWebsiteSchema: z.ZodType<TemplateReferenceOrganizationAllowedWebsite> =
    TemplateOrganizationAllowedWebsiteSchema;

export const OrganizationAllowedWebsiteSchema: z.ZodType<OrganizationAllowedWebsite> =
    z.discriminatedUnion('source', [
        LocalOrganizationAllowedWebsiteSchema,
        TemplateOrganizationAllowedWebsiteSchema,
    ]);

export const ListOrganizationAllowedWebsitesResponseSchema: z.ZodType<ListOrganizationAllowedWebsitesResponse> =
    z.object({
        websites: z.array(OrganizationAllowedWebsiteSchema),
        capabilities: OrganizationAllowedWebsiteCollectionCapabilitiesSchema,
    });

export const CreateLocalOrganizationAllowedWebsiteRequestSchema: z.ZodType<CreateLocalOrganizationAllowedWebsiteRequest> =
    z
        .object({
            website_config: AllowedWebsiteSchemaUnmanaged,
            display_order: z.number().int().nonnegative().nullable().optional(),
        })
        .strict();

export const ReferenceAllowedWebsiteTemplateToOrganizationRequestSchema: z.ZodType<ReferenceAllowedWebsiteTemplateToOrganizationRequest> =
    z
        .object({
            allowed_website_template_uuid: z.string().uuid(),
            display_order: z.number().int().nonnegative().nullable().optional(),
        })
        .strict();

export const UpdateOrganizationAllowedWebsiteRequestSchema: z.ZodType<UpdateOrganizationAllowedWebsiteRequest> =
    z
        .object({
            website_config: AllowedWebsiteSchemaUnmanaged.optional(),
            display_order: z.number().int().nonnegative().nullable().optional(),
        })
        .strict()
        .refine(
            (request) =>
                request.website_config !== undefined ||
                request.display_order !== undefined,
            'At least one organization website field must be updated.'
        );

export const OrganizationAllowedWebsiteMutationResponseSchema: z.ZodType<OrganizationAllowedWebsiteMutationResponse> =
    z.object({
        organization_website: OrganizationAllowedWebsiteSchema,
    });

export const DeleteOrganizationAllowedWebsiteResponseSchema: z.ZodType<DeleteOrganizationAllowedWebsiteResponse> =
    z.object({
        organization_allowed_website_uuid: z.string().uuid(),
        deleted: z.literal(true),
    });

export const PromoteOrganizationWebsiteToTemplateRequestSchema: z.ZodType<PromoteOrganizationWebsiteToTemplateRequest> =
    z
        .object({
            organization_allowed_website_uuid: z.string().uuid(),
            ipx_system_id: z.number().int().nonnegative(),
            template_name: z.string().trim().min(1),
        })
        .strict();

export const PromoteOrganizationWebsiteToTemplateResponseSchema: z.ZodType<PromoteOrganizationWebsiteToTemplateResponse> =
    z.object({
        template: AllowedWebsiteTemplateListItemSchema,
        organization_website: OrganizationAllowedWebsiteSchema,
    });
